import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useCreateIssue } from "../issues/hooks";

type Props = {
  stationId: string;
  stationName: string;
  onClose: () => void;
};

const MIN = 5;
const MAX = 1000;

export function StatusPicker({ stationId, stationName, onClose }: Props) {
  const { t } = useTranslation();
  const [description, setDescription] = useState("");
  const createIssue = useCreateIssue(stationId);

  const valid = description.length >= MIN && description.length <= MAX;

  function handleSubmit() {
    if (!valid) return;
    createIssue.mutate(
      { station_id: stationId, description },
      {
        onSuccess: () => {
          setDescription("");
          onClose();
        },
      },
    );
  }

  return (
    <div className="space-y-3 rounded-lg border border-line bg-surface p-4">
      <header className="space-y-1">
        <p className="label-caps text-fg-mute">{t("issues.form_eyebrow")}</p>
        <h2 className="text-base font-semibold text-fg">{stationName}</h2>
      </header>
      <label className="label-caps block text-fg-mute" htmlFor="issue-desc">
        {t("issues.form_label")}
      </label>
      <textarea
        id="issue-desc"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        minLength={MIN}
        maxLength={MAX}
        rows={4}
        placeholder={t("issues.form_placeholder")}
        className="w-full rounded-md border border-line bg-bg-deep p-3 text-fg focus:border-volt focus:outline-none"
      />
      {description.length > 0 && description.length < MIN && (
        <p className="text-xs text-amber">{t("issues.form_min_chars", { min: MIN })}</p>
      )}
      {createIssue.isError && (
        <p className="text-xs text-ember">{t("issues.form_failed")}</p>
      )}
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="rounded-full px-4 py-2 text-sm text-fg-mute hover:text-fg"
        >
          {t("issues.cancel")}
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!valid || createIssue.isPending}
          className="volt-stroke-left rounded-full bg-volt px-5 py-2 text-sm font-semibold text-on-volt disabled:bg-surface-raised disabled:text-fg-faint"
        >
          {createIssue.isPending ? t("issues.submit_pending") : t("issues.submit")}
        </button>
      </div>
    </div>
  );
}
